import React, { useState, Fragment } from 'react';
import { Card } from '../Shared/Card';
import { SearchIcon, FilterIcon, ClockIcon, FileTextIcon, UserIcon, DatabaseIcon, GitBranchIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon, ExternalLinkIcon, ArrowRightIcon, CalendarIcon } from 'lucide-react';
import { LineageGraph } from './LineageGraph';
type FieldType = 'Property' | 'Financial' | 'Credit' | 'Borrower' | 'Documents' | 'Underwriting';
interface FieldLineage {
  id: string;
  name: string;
  currentValue: string;
  source: string;
  sourceIcon: string;
  transformation: string;
  lastModified: string;
  modifiedBy: string;
  notes: string;
  fieldType: FieldType;
  hasHistory: boolean;
  historyCount?: number;
}
interface FieldHistory {
  date: string;
  value: string;
  source: string;
  user: string;
  action: string;
  notes?: string;
}
export function LineageView() {
  const [selectedLoan, setSelectedLoan] = useState<string>('005312');
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [showOnlyModified, setShowOnlyModified] = useState<boolean>(false);
  const [selectedFieldType, setSelectedFieldType] = useState<FieldType | 'All'>('All');
  const [activeTab, setActiveTab] = useState<'table' | 'timeline' | 'graph'>('table');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedField, setExpandedField] = useState<string | null>(null);
  // Sample loan options
  const loanOptions = [{
    id: '005312',
    label: 'Loan #005312 - Alex Young (Fix-and-flip)'
  }, {
    id: '005324',
    label: 'Loan #005324 - Maria Chen (DSCR)'
  }, {
    id: '005355',
    label: 'Loan #005355 - Robert Johnson (DSCR)'
  }, {
    id: '005342',
    label: 'Loan #005342 - Sarah Miller (Fix-and-flip)'
  }, {
    id: '005331',
    label: 'Loan #005331 - James Wilson (Bridge)'
  }];
  // Sample field lineage data
  const fieldLineageData: FieldLineage[] = [{
    id: 'property-value',
    name: 'Property Value',
    currentValue: '$875,000',
    source: 'Appraisal Document',
    sourceIcon: 'FileText',
    transformation: 'Rounded to nearest $5K',
    lastModified: '2024-09-15 14:21 EST',
    modifiedBy: 'Structuring Agent',
    notes: 'Appraisal parsed from PDF',
    fieldType: 'Property',
    hasHistory: true,
    historyCount: 3
  }, {
    id: 'ltv',
    name: 'LTV',
    currentValue: '72%',
    source: 'Derived',
    sourceIcon: 'Calculator',
    transformation: 'Calc: Loan / Appraised Val',
    lastModified: '2024-09-15 14:21 EST',
    modifiedBy: 'System',
    notes: 'Used post-rounding value',
    fieldType: 'Financial',
    hasHistory: true,
    historyCount: 2
  }, {
    id: 'dscr',
    name: 'DSCR',
    currentValue: '1.25',
    source: 'Borrower Input',
    sourceIcon: 'User',
    transformation: 'Normalized to 2 decimals',
    lastModified: '2024-09-15 14:20 EST',
    modifiedBy: 'Loan Officer',
    notes: 'Overridden from 1.22',
    fieldType: 'Financial',
    hasHistory: true,
    historyCount: 4
  }, {
    id: 'fico',
    name: 'FICO Score',
    currentValue: '695',
    source: 'TransUnion API',
    sourceIcon: 'Database',
    transformation: 'Unchanged',
    lastModified: '2024-09-15 13:45 EST',
    modifiedBy: 'Credit Agent',
    notes: 'Returned via TU pull',
    fieldType: 'Credit',
    hasHistory: false
  }, {
    id: 'rent-roll',
    name: 'Rent Roll Monthly',
    currentValue: '$7,400',
    source: 'Lease Agreement Doc',
    sourceIcon: 'FileText',
    transformation: 'Summed from 3 leases',
    lastModified: '2024-09-14 11:03 EST',
    modifiedBy: 'Parsing Agent',
    notes: 'Includes ADU income',
    fieldType: 'Property',
    hasHistory: true,
    historyCount: 2
  }, {
    id: 'loan-amount',
    name: 'Loan Amount',
    currentValue: '$630,000',
    source: 'Loan Agreement',
    sourceIcon: 'FileText',
    transformation: 'Unchanged',
    lastModified: '2024-09-14 10:30 EST',
    modifiedBy: 'Loan Officer',
    notes: 'Final approved amount',
    fieldType: 'Financial',
    hasHistory: true,
    historyCount: 3
  }, {
    id: 'borrower-experience',
    name: 'Borrower Experience',
    currentValue: '5 properties',
    source: 'Borrower Declaration',
    sourceIcon: 'User',
    transformation: 'Verified against property records',
    lastModified: '2024-09-13 16:42 EST',
    modifiedBy: 'Underwriter',
    notes: 'Experience verified with county records',
    fieldType: 'Borrower',
    hasHistory: true,
    historyCount: 1
  }, {
    id: 'property-type',
    name: 'Property Type',
    currentValue: 'Single Family Residence',
    source: 'Property Records',
    sourceIcon: 'Database',
    transformation: 'Standardized terminology',
    lastModified: '2024-09-13 15:20 EST',
    modifiedBy: 'System',
    notes: 'Converted from "SFR" to full text',
    fieldType: 'Property',
    hasHistory: false
  }, {
    id: 'eligibility-flag',
    name: 'DBRS Eligibility',
    currentValue: 'Eligible',
    source: 'Rule Engine',
    sourceIcon: 'CheckCircle',
    transformation: 'Derived from multiple criteria',
    lastModified: '2024-09-15 14:30 EST',
    modifiedBy: 'System',
    notes: 'All eligibility criteria met',
    fieldType: 'Underwriting',
    hasHistory: true,
    historyCount: 2
  }];
  // Sample field history data for property value
  const propertyValueHistory: FieldHistory[] = [{
    date: '2024-09-15 14:21 EST',
    value: '$875,000',
    source: 'Appraisal Document',
    user: 'Structuring Agent',
    action: 'Rounded',
    notes: 'Rounded to nearest $5K from $873,500'
  }, {
    date: '2024-09-15 11:45 EST',
    value: '$873,500',
    source: 'Appraisal Document',
    user: 'Document Parser',
    action: 'Extracted',
    notes: 'Value extracted from appraisal PDF'
  }, {
    date: '2024-09-14 09:30 EST',
    value: '$850,000',
    source: 'Borrower Input',
    user: 'Loan Officer',
    action: 'Entered',
    notes: 'Initial value provided by borrower'
  }];
  // Filter fields based on search query and filters
  const filteredFields = fieldLineageData.filter(field => {
    // Filter by field type
    if (selectedFieldType !== 'All' && field.fieldType !== selectedFieldType) {
      return false;
    }
    // Filter by modified fields only
    if (showOnlyModified && !field.hasHistory) {
      return false;
    }
    // Filter by search query
    if (searchQuery && !field.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  // Get field history for the expanded field
  const getFieldHistory = (fieldId: string) => {
    if (fieldId === 'property-value') {
      return propertyValueHistory;
    }
    return [];
  };
  const toggleFieldExpansion = (fieldId: string) => {
    if (expandedField === fieldId) {
      setExpandedField(null);
    } else {
      setExpandedField(fieldId);
    }
  };
  const getSourceIcon = (iconName: string) => {
    switch (iconName) {
      case 'FileText':
        return <FileTextIcon className="w-4 h-4" data-id="element-976" />;
      case 'User':
        return <UserIcon className="w-4 h-4" data-id="element-977" />;
      case 'Database':
        return <DatabaseIcon className="w-4 h-4" data-id="element-978" />;
      case 'Calculator':
        return <GitBranchIcon className="w-4 h-4" data-id="element-979" />;
      case 'CheckCircle':
        return <InfoIcon className="w-4 h-4" data-id="element-980" />;
      default:
        return <FileTextIcon className="w-4 h-4" data-id="element-981" />;
    }
  };
  return <div className="space-y-6" data-id="element-982">
      <div className="flex justify-between items-center" data-id="element-983">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-984">
          Data Lineage View
        </h2>
        <div className="flex space-x-3" data-id="element-985">
          <button className="px-4 py-2 bg-background border border-border rounded-full text-foreground/70 hover:bg-muted flex items-center text-sm transition-colors" data-id="element-986">
            <FilterIcon className="w-4 h-4 mr-2" data-id="element-987" />
            Export Report
          </button>
          <button className="px-4 py-2 bg-success rounded-full text-foreground/70 hover:bg-success/80 flex items-center text-sm transition-colors" data-id="element-988">
            <GitBranchIcon className="w-4 h-4 mr-2" data-id="element-989" />
            Trace Selected Fields
          </button>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-md flex items-start" data-id="element-990">
        <InfoIcon className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-991" />
        <div data-id="element-992">
          <h3 className="font-medium text-blue-800" data-id="element-993">About Data Lineage</h3>
          <p className="text-sm text-blue-700 mt-1" data-id="element-994">
            Data lineage traces the origin, transformations, and modifications
            of each data field. This provides transparency for auditing,
            compliance, and troubleshooting purposes.
          </p>
        </div>
      </div>
      <Card data-id="element-995">
        <div className="space-y-4" data-id="element-996">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-997">
            <div className="w-full md:w-1/2" data-id="element-998">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-999">
                Select Loan
              </label>
              <select className="w-full border border-gray-300 rounded-md px-3 py-2" value={selectedLoan} onChange={e => setSelectedLoan(e.target.value)} data-id="element-1000">
                {loanOptions.map(loan => <option key={loan.id} value={loan.id} data-id="element-1001">
                    {loan.label}
                  </option>)}
              </select>
            </div>
            <div className="w-full md:w-1/2 relative" data-id="element-1002">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1003">
                Search Fields
              </label>
              <div className="relative" data-id="element-1004">
                <input type="text" className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2" placeholder="Search by field name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1005" />
                <SearchIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" data-id="element-1006" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200" data-id="element-1007">
            <span className="text-sm font-medium text-gray-700" data-id="element-1008">
              Field Type:
            </span>
            <div className="flex flex-wrap gap-2" data-id="element-1009">
              {['All', 'Property', 'Financial', 'Credit', 'Borrower', 'Documents', 'Underwriting'].map(type => <button key={type} className={`px-3 py-1 text-xs rounded-full ${selectedFieldType === type ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setSelectedFieldType(type as FieldType | 'All')} data-id="element-1010">
                  {type}
                </button>)}
            </div>
            <div className="ml-auto flex items-center" data-id="element-1011">
              <label className="flex items-center text-sm text-gray-700" data-id="element-1012">
                <input type="checkbox" className="mr-2 h-4 w-4 text-blue-600 rounded" checked={showOnlyModified} onChange={() => setShowOnlyModified(!showOnlyModified)} data-id="element-1013" />
                Show Only Modified Fields
              </label>
            </div>
          </div>
          <div className="border-b border-gray-200" data-id="element-1014">
            <nav className="-mb-px flex space-x-6" data-id="element-1015">
              <button onClick={() => setActiveTab('table')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'table' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1016">
                Field Lineage Table
              </button>
              <button onClick={() => setActiveTab('timeline')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'timeline' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1017">
                Timeline View
              </button>
              <button onClick={() => setActiveTab('graph')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'graph' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1018">
                Transformation Graph
              </button>
            </nav>
          </div>
          {activeTab === 'table' && <div className="overflow-x-auto" data-id="element-1019">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1020">
                <thead className="bg-gray-50" data-id="element-1021">
                  <tr data-id="element-1022">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1023">
                      Field
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1024">
                      Current Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1025">
                      Source
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1026">
                      Transformation
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1027">
                      Last Modified
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1028">
                      Modified By
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1029">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1030">
                  {filteredFields.map(field => <Fragment key={field.id} data-id="element-1031">
                      <tr className={`hover:bg-gray-50 cursor-pointer ${expandedField === field.id ? 'bg-blue-50' : ''}`} onClick={() => toggleFieldExpansion(field.id)} data-id="element-1032">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1033">
                          <div className="flex items-center" data-id="element-1034">
                            {field.hasHistory ? <ChevronDownIcon className={`w-4 h-4 mr-2 text-gray-400 transition-transform ${expandedField === field.id ? 'transform rotate-0' : 'transform rotate-270'}`} data-id="element-1035" /> : <div className="w-4 h-4 mr-2" data-id="element-1036" />}
                            <div data-id="element-1037">
                              <div className="text-sm font-medium text-gray-900" data-id="element-1038">
                                {field.name}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-1039">
                                {field.fieldType}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1040">
                          {field.currentValue}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1041">
                          <div className="flex items-center text-sm text-gray-900" data-id="element-1042">
                            <span className="flex-shrink-0 h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center mr-2" data-id="element-1043">
                              {getSourceIcon(field.sourceIcon)}
                            </span>
                            {field.source}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1044">
                          {field.transformation}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1045">
                          {field.lastModified}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1046">
                          {field.modifiedBy}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1047">
                          {field.notes}
                        </td>
                      </tr>
                      {expandedField === field.id && field.hasHistory && <tr className="bg-gray-50" data-id="element-1048">
                          <td colSpan={7} className="px-6 py-4" data-id="element-1049">
                            <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-1050">
                              Field History
                            </div>
                            <div className="space-y-3 max-h-64 overflow-y-auto" data-id="element-1051">
                              {getFieldHistory(field.id).map((history, index) => <div key={index} className="flex items-start border-l-2 border-blue-400 pl-4 pb-3" data-id="element-1052">
                                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3" data-id="element-1053">
                                      <ClockIcon className="h-4 w-4 text-blue-600" data-id="element-1054" />
                                    </div>
                                    <div className="flex-1" data-id="element-1055">
                                      <div className="flex items-center justify-between" data-id="element-1056">
                                        <div className="text-sm font-medium text-gray-900" data-id="element-1057">
                                          {history.action}: {history.value}
                                        </div>
                                        <div className="text-xs text-gray-500" data-id="element-1058">
                                          {history.date}
                                        </div>
                                      </div>
                                      <div className="mt-1 flex items-center text-xs text-gray-500" data-id="element-1059">
                                        <span className="mr-2" data-id="element-1060">
                                          Source: {history.source}
                                        </span>
                                        <span data-id="element-1061">User: {history.user}</span>
                                      </div>
                                      {history.notes && <div className="mt-1 text-xs text-gray-500" data-id="element-1062">
                                          Note: {history.notes}
                                        </div>}
                                    </div>
                                  </div>)}
                            </div>
                            <button className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1063">
                              View full history{' '}
                              <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-1064" />
                            </button>
                          </td>
                        </tr>}
                    </Fragment>)}
                </tbody>
              </table>
            </div>}
          {activeTab === 'timeline' && <div className="p-4 border border-gray-200 rounded-lg" data-id="element-1065">
              <div className="flex justify-between items-center mb-4" data-id="element-1066">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-1067">
                  Field Timeline View
                </h3>
                <div className="flex items-center" data-id="element-1068">
                  <select className="border border-gray-300 rounded-md px-3 py-1.5 text-sm" data-id="element-1069">
                    <option data-id="element-1070">Property Value</option>
                    <option data-id="element-1071">DSCR</option>
                    <option data-id="element-1072">LTV</option>
                    <option data-id="element-1073">Loan Amount</option>
                  </select>
                </div>
              </div>
              <div className="relative" data-id="element-1074">
                <div className="absolute left-9 top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-1075"></div>
                <div className="space-y-8" data-id="element-1076">
                  {propertyValueHistory.map((history, index) => <div key={index} className="relative flex items-start" data-id="element-1077">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 border-2 border-white shadow z-10" data-id="element-1078">
                        <CalendarIcon className="h-4 w-4 text-blue-600" data-id="element-1079" />
                      </div>
                      <div className="ml-6 bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex-1" data-id="element-1080">
                        <div className="flex justify-between items-start" data-id="element-1081">
                          <div data-id="element-1082">
                            <span className="text-xs text-gray-500" data-id="element-1083">
                              {history.date}
                            </span>
                            <h4 className="text-sm font-medium text-gray-900 mt-1" data-id="element-1084">
                              {history.action}: {history.value}
                            </h4>
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800" data-id="element-1085">
                            {history.source}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-600" data-id="element-1086">
                          <p data-id="element-1087">
                            <span className="font-medium" data-id="element-1088">User:</span>{' '}
                            {history.user}
                          </p>
                          {history.notes && <p className="mt-1" data-id="element-1089">
                              <span className="font-medium" data-id="element-1090">Notes:</span>{' '}
                              {history.notes}
                            </p>}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}
          {activeTab === 'graph' && <div className="p-4 border border-gray-200 rounded-lg" data-id="element-1091">
              <div className="flex justify-between items-center mb-4" data-id="element-1092">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-1093">
                  Field Transformation Graph
                </h3>
                <div className="flex items-center" data-id="element-1094">
                  <select className="border border-gray-300 rounded-md px-3 py-1.5 text-sm" data-id="element-1095">
                    <option data-id="element-1096">Property Value</option>
                    <option data-id="element-1097">DSCR</option>
                    <option data-id="element-1098">LTV</option>
                    <option data-id="element-1099">Loan Amount</option>
                  </select>
                </div>
              </div>
              <div className="h-64 bg-white" data-id="element-1100">
                <LineageGraph data-id="element-1101" />
              </div>
              <div className="mt-4 bg-gray-50 p-3 rounded-md" data-id="element-1102">
                <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-1103">
                  Transformation Path
                </div>
                <div className="flex items-center text-sm text-gray-600" data-id="element-1104">
                  <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded" data-id="element-1105">
                    Borrower Input
                  </div>
                  <ArrowRightIcon className="mx-2 h-4 w-4 text-gray-400" data-id="element-1106" />
                  <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded" data-id="element-1107">
                    Loan Officer
                  </div>
                  <ArrowRightIcon className="mx-2 h-4 w-4 text-gray-400" data-id="element-1108" />
                  <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded" data-id="element-1109">
                    Appraisal Document
                  </div>
                  <ArrowRightIcon className="mx-2 h-4 w-4 text-gray-400" data-id="element-1110" />
                  <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded" data-id="element-1111">
                    Structuring Agent
                  </div>
                  <ArrowRightIcon className="mx-2 h-4 w-4 text-gray-400" data-id="element-1112" />
                  <div className="px-2 py-1 bg-green-100 text-green-800 rounded" data-id="element-1113">
                    Final Value
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </Card>
    </div>;
}